// Audio Pathway Configuration
// This file contains all the configuration for different audio pathways
// and can be easily updated without modifying the core audio mapping logic

export interface AudioPathwayConfig {
  pathwayId: string;
  name: string;
  basePath: string;
  lessonMapping: { [lessonId: string]: string };
  categories: string[];
  useStaticFiles: boolean;
  useGanjiAdabi: boolean;
  fallbackToTTS: boolean;
  description?: string;
}

export interface AudioFileMapping {
  persianText: string;
  englishFilename: string;
  category: 'wordbank' | 'dialogue' | 'phrases' | 'activities';
  lessonId: string;
}

// Configuration for different pathways
export const PATHWAY_CONFIGS: AudioPathwayConfig[] = [
  {
    pathwayId: '6875e2c70e9af9bca4a60a18',
    name: 'Clarification Pathway',
    basePath: '/audio/6875e2c70e9af9bca4a60a18',
    lessonMapping: {
      'lesson-2': 'lesson-2',
      'lesson-4': 'lesson-4'
    },
    categories: ['dialogue', 'wordbank'],
    useStaticFiles: true,
    useGanjiAdabi: false,
    fallbackToTTS: true,
    description: 'Learning how to ask for clarification and repetition in Persian'
  },
  {
    pathwayId: '6875e2c70e9af9bca4a60a0f',
    name: 'All About Pathway',
    basePath: '/audio/6875e2c70e9af9bca4a60a0f',
    lessonMapping: {},
    categories: ['dialogue', 'wordbank', 'phrases'],
    useStaticFiles: true,
    useGanjiAdabi: false,
    fallbackToTTS: true,
    description: 'Cultural topics and everyday conversations in Persian'
  },
  {
    pathwayId: '68749a870819400c3be14ad4',
    name: 'Tehran Cultural Journey',
    basePath: '/audio/tehran-cultural-lesson',
    lessonMapping: {
      'tehran-cultural-exploration': 'tehran-cultural-exploration'
    },
    categories: ['dialogue', 'wordbank', 'activities'],
    useStaticFiles: true,
    useGanjiAdabi: true,
    fallbackToTTS: true,
    description: 'Explore Tehran\'s culture, landmarks, and daily life while learning Persian'
  },
  {
    pathwayId: '6876c06cf4cfbaec65f9ba59',
    name: '1001 Persian Nights',
    basePath: '/audio/6876c06cf4cfbaec65f9ba59',
    lessonMapping: {},
    categories: ['dialogue', 'wordbank'],
    useStaticFiles: true,
    useGanjiAdabi: false,
    fallbackToTTS: true,
    description: 'Story-based learning with Persian folktales and vocabulary'
  },
  {
    pathwayId: '6875e8eebb064190db6158a0',
    name: 'Advanced Persian Grammar',
    basePath: '/audio/6875e8eebb064190db6158a0',
    lessonMapping: {
      'a0b6147f-d894-4b4b-9356-efbdffdd3055': 'Advanced Personal Pronouns and Possession',
      'c69f00eb-abf6-4bb8-a7d3-11490e4b1224': 'Verb Conjugation and Tense Mastery',
      '47c36ca7-6762-4c0d-8305-ffbca3d79fbe': 'Complex Sentence Structures and Subordination',
      '01683446-44ff-45e8-a155-b001205ee92d': 'Advanced Grammatical Constructions and Literary Forms'
    },
    categories: ['dialogue', 'wordbank'],
    useStaticFiles: false,
    useGanjiAdabi: true,
    fallbackToTTS: true,
    description: 'Advanced grammar concepts and complex sentence structures'
  },
  {
    pathwayId: 'easy-persian-alphabet',
    name: 'Easy Persian Alphabet',
    basePath: '/audio/easy-persian-alphabet',
    lessonMapping: {},
    categories: ['dialogue', 'wordbank'],
    useStaticFiles: false,
    useGanjiAdabi: true,
    fallbackToTTS: true,
    description: 'Learning the Persian alphabet and basic pronunciation'
  },
  {
    pathwayId: 'grammar-fundamentals-pathway',
    name: 'Grammar Fundamentals',
    basePath: '/audio/grammar-fundamentals-pathway',
    lessonMapping: {},
    categories: ['wordbank', 'dialogue'],
    useStaticFiles: true,
    useGanjiAdabi: false,
    fallbackToTTS: true,
    description: 'Basic grammar concepts and essential vocabulary'
  },
  {
    pathwayId: 'unknown-bzi8z1wve',
    name: 'Grammar 11',
    basePath: '/audio/unknown-bzi8z1wve',
    lessonMapping: {},
    categories: ['wordbank', 'dialogue'],
    useStaticFiles: true,
    useGanjiAdabi: false,
    fallbackToTTS: true,
    description: 'Passive voice and advanced grammar structures'
  },
  {
    pathwayId: 'kitchen-farsi',
    name: 'Kitchen Farsi',
    basePath: '/audio/kitchen-farsi',
    lessonMapping: {},
    categories: ['wordbank', 'dialogue', 'phrases'],
    useStaticFiles: true,
    useGanjiAdabi: false,
    fallbackToTTS: true,
    description: 'Cooking and kitchen-related vocabulary and phrases'
  },
  {
    pathwayId: '6875e2c70e9af9bca4a60a3f',
    name: 'Level 1 Persian',
    basePath: '/audio/6875e2c70e9af9bca4a60a3f',
    lessonMapping: {},
    categories: ['wordbank', 'dialogue', 'phrases', 'activities'],
    useStaticFiles: true,
    useGanjiAdabi: false,
    fallbackToTTS: true,
    description: 'Basic Persian vocabulary and phrases for beginners'
  },
  {
    pathwayId: '1332d56b-fbcd-480d-a56c-7241c74269b5',
    name: 'Tea, Flowers, and Forever',
    basePath: '/audio/1332d56b-fbcd-480d-a56c-7241c74269b5',
    lessonMapping: {},
    categories: ['wordbank', 'dialogue', 'scenes', 'activities'],
    useStaticFiles: true,
    useGanjiAdabi: false,
    fallbackToTTS: true,
    description: 'Sweet Persian words from a Khastegāri Night'
  },
  {
    pathwayId: 'persian-politeness-masterclass-ep1',
    name: 'Persian Politeness Masterclass',
    basePath: '/audio/persian-politeness-masterclass-ep1',
    lessonMapping: {},
    categories: ['phrases', 'wordbank', 'dialogue', 'activities'],
    useStaticFiles: true,
    useGanjiAdabi: false,
    fallbackToTTS: true,
    description: 'Episode 1: Ta\'arof 101 - The Dance of Persian Politeness'
  },
  {
    pathwayId: '6875e8eebb064190db6157e2',
    name: 'پرسیدن آزاد است، جواب دادن اجباری!',
    basePath: '/audio/6875e8eebb064190db6157e2',
    lessonMapping: {},
    categories: ['phrases', 'wordbank'],
    useStaticFiles: true,
    useGanjiAdabi: false,
    fallbackToTTS: true,
    description: 'Asking questions freely - answering is mandatory!'
  },
  {
    pathwayId: '6876c06cf4cfbaec65f9bad4',
    name: 'Text Me in Farsi',
    basePath: '/audio/6876c06cf4cfbaec65f9bad4',
    lessonMapping: {},
    categories: ['phrases'],
    useStaticFiles: true,
    useGanjiAdabi: false,
    fallbackToTTS: true,
    description: 'Modern Persian texting and emoji usage'
  },
  {
    pathwayId: 'clock-time-pathway',
    name: 'Clock & Time Mastery',
    basePath: '/audio/clock-time-pathway',
    lessonMapping: {},
    categories: ['wordbank', 'scenes'],
    useStaticFiles: true,
    useGanjiAdabi: false,
    fallbackToTTS: true,
    description: 'Learning to tell time and discuss schedules in Persian'
  }
];

// Audio file mappings for specific lessons
// This can be expanded as needed without changing the core logic
export const AUDIO_FILE_MAPPINGS: { [pathwayId: string]: { [lessonId: string]: AudioFileMapping[] } } = {
  '6875e2c70e9af9bca4a60a18': {
    'lesson-2': [
      {
        persianText: 'آهسته صحبت کن',
        englishFilename: 'can-you-speak-slowly',
        category: 'dialogue',
        lessonId: 'lesson-2'
      },
      {
        persianText: 'خوب نشنیدم',
        englishFilename: 'i-didnt-hear-you-well',
        category: 'dialogue',
        lessonId: 'lesson-2'
      },
      {
        persianText: 'دوباره بگو',
        englishFilename: 'can-you-say-it-again',
        category: 'dialogue',
        lessonId: 'lesson-2'
      },
      {
        persianText: 'معنی اش چیست',
        englishFilename: 'what-does-it-mean',
        category: 'dialogue',
        lessonId: 'lesson-2'
      }
    ]
  },
  '6875e8eebb064190db6158a0': {
    'a0b6147f-d894-4b4b-9356-efbdffdd3055': [
      {
        persianText: 'من دانشجوی ادبیات فارسی هستم و علاقه‌مند به شعر کلاسیک ایران.',
        englishFilename: 'i-am-a-persian-literature-student-and-interested-in-classical-iranian-poetry',
        category: 'dialogue',
        lessonId: 'a0b6147f-d894-4b4b-9356-efbdffdd3055'
      },
      {
        persianText: 'شما در مورد خودتان چه می‌گویید؟',
        englishFilename: 'what-do-you-say-about-yourself',
        category: 'dialogue',
        lessonId: 'a0b6147f-d894-4b4b-9356-efbdffdd3055'
      },
      {
        persianText: 'آیا شما با آثار حافظ آشنا هستید؟',
        englishFilename: 'are-you-familiar-with-hafezs-works',
        category: 'dialogue',
        lessonId: 'a0b6147f-d894-4b4b-9356-efbdffdd3055'
      },
      {
        persianText: 'بله، من دیوان حافظ را مطالعه کرده‌ام و آن را بسیار دوست دارم.',
        englishFilename: 'yes-i-have-studied-hafezs-divan-and-i-like-it-very-much',
        category: 'dialogue',
        lessonId: 'a0b6147f-d894-4b4b-9356-efbdffdd3055'
      }
    ]
  },
  'easy-persian-alphabet': {
    '13cbf7a6-376c-45d2-a608-b41a917e9756': [
      {
        persianText: 'الف',
        englishFilename: 'alef',
        category: 'wordbank',
        lessonId: '13cbf7a6-376c-45d2-a608-b41a917e9756'
      },
      {
        persianText: 'آب',
        englishFilename: 'water',
        category: 'wordbank',
        lessonId: '13cbf7a6-376c-45d2-a608-b41a917e9756'
      },
      {
        persianText: 'آسمان',
        englishFilename: 'sky',
        category: 'wordbank',
        lessonId: '13cbf7a6-376c-45d2-a608-b41a917e9756'
      }
    ]
  }
};

// Helper function to get pathway config by ID
export const getPathwayConfig = (pathwayId: string): AudioPathwayConfig | undefined => {
  return PATHWAY_CONFIGS.find(c => c.pathwayId === pathwayId);
};

// Helper function to get all pathway configs
export const getAllPathwayConfigs = (): AudioPathwayConfig[] => {
  return [...PATHWAY_CONFIGS];
};

// Helper function to get audio file mappings for a pathway and lesson
export const getAudioFileMappings = (pathwayId: string, lessonId: string): AudioFileMapping[] => {
  return AUDIO_FILE_MAPPINGS[pathwayId]?.[lessonId] || [];
};

// Helper function to add new pathway configuration
export const addPathwayConfig = (config: AudioPathwayConfig): void => {
  const existingIndex = PATHWAY_CONFIGS.findIndex(c => c.pathwayId === config.pathwayId);
  if (existingIndex >= 0) {
    PATHWAY_CONFIGS[existingIndex] = config;
  } else {
    PATHWAY_CONFIGS.push(config);
  }
};

// Helper function to add new audio file mappings
export const addAudioFileMappings = (
  pathwayId: string, 
  lessonId: string, 
  mappings: AudioFileMapping[]
): void => {
  if (!AUDIO_FILE_MAPPINGS[pathwayId]) {
    AUDIO_FILE_MAPPINGS[pathwayId] = {};
  }
  if (!AUDIO_FILE_MAPPINGS[pathwayId][lessonId]) {
    AUDIO_FILE_MAPPINGS[pathwayId][lessonId] = [];
  }
  AUDIO_FILE_MAPPINGS[pathwayId][lessonId].push(...mappings);
};

export default PATHWAY_CONFIGS;
